import React from 'react';
import { createPortal } from 'react-dom';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { openImage } from '../../../store/chatSlice';
import { FilePreview, FullscreenDataModal } from '../input/upload/FilePreview';
import type { FullscreenTableData, FullscreenTextData } from '../input/upload/FilePreview';
import { useTheme } from '../../Theme/ThemeProvider';
import { arrayBufferToBase64 } from '../../../utils/base64.utils';

const PREVIEW_DIALOG_ANIMATION_MS = 380;

export type PreviewableFileDataSource = 'uploaded' | 'data-url';

interface ResolvedPreviewData {
  canPreview: boolean;
  isImage: boolean;
  previewKind: 'image' | 'pdf' | 'table' | 'text' | 'document' | 'none';
  previewFileData?: string | ArrayBuffer;
  previewFileType?: string;
}

interface PreviewableFileAttachmentProps {
  fileName: string;
  fileType?: string;
  fileData?: string;
  dataSource?: PreviewableFileDataSource;
  children: (args: {
    canPreview: boolean;
    isImage: boolean;
    openPreview: () => void;
    previewFileData?: string | ArrayBuffer;
    previewFileType?: string;
    previewKind: 'image' | 'pdf' | 'table' | 'text' | 'document' | 'none';
  }) => React.ReactNode;
}

const MIME_BY_EXTENSION: Record<string, string> = {
  csv: 'text/csv',
  gif: 'image/gif',
  htm: 'text/html',
  html: 'text/html',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  json: 'application/json',
  md: 'text/markdown',
  pdf: 'application/pdf',
  png: 'image/png',
  svg: 'image/svg+xml',
  txt: 'text/plain',
  tsv: 'text/tab-separated-values',
  webp: 'image/webp',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function getFileExtension(fileName: string) {
  return fileName.split('.').pop()?.toLowerCase() || '';
}

function inferMimeType(fileName: string, fileType?: string) {
  if (fileType) return fileType;
  return MIME_BY_EXTENSION[getFileExtension(fileName)] || 'application/octet-stream';
}

function parseDataUrl(dataUrl?: string) {
  if (!dataUrl || !dataUrl.startsWith('data:')) return null;
  const commaIndex = dataUrl.indexOf(',');
  if (commaIndex === -1) return null;

  const metadata = dataUrl.slice(5, commaIndex);
  const payload = dataUrl.slice(commaIndex + 1);
  const parts = metadata.split(';').filter(Boolean);
  const mimeType = parts[0] || '';
  const isBase64 = parts.includes('base64');

  return { mimeType, payload, isBase64, dataUrl };
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const sanitized = base64.replace(/\s+/g, '');
  const binary = atob(sanitized);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

function base64ToUtf8(base64: string): string {
  const buffer = base64ToArrayBuffer(base64);
  try {
    return new TextDecoder('utf-8').decode(buffer);
  } catch {
    const bytes = new Uint8Array(buffer);
    let fallback = '';
    for (let i = 0; i < bytes.length; i++) {
      fallback += String.fromCharCode(bytes[i]);
    }
    return fallback;
  }
}

function isImageType(fileName: string, mimeType: string) {
  return mimeType.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(fileName);
}

function isPdfType(fileName: string, mimeType: string) {
  return mimeType === 'application/pdf' || /\.pdf$/i.test(fileName);
}

function isExcelType(fileName: string, mimeType: string) {
  return /\.(xlsx|xls)$/i.test(fileName)
    || mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    || mimeType === 'application/vnd.ms-excel';
}

function isTextType(fileName: string, mimeType: string) {
  return /\.(csv|tsv|txt|log|md|markdown|json|html|htm)$/i.test(fileName)
    || mimeType.startsWith('text/')
    || mimeType === 'application/json'
    || mimeType === 'application/csv';
}

function isTableType(fileName: string, mimeType: string) {
  return isExcelType(fileName, mimeType)
    || /\.(csv|tsv)$/i.test(fileName)
    || mimeType === 'text/csv'
    || mimeType === 'text/tab-separated-values'
    || mimeType === 'application/csv';
}

function isConvertibleDocType(fileName: string, mimeType: string) {
  return /\.(docx?|pptx?)$/i.test(fileName)
    || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || mimeType === 'application/msword'
    || mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
    || mimeType === 'application/vnd.ms-powerpoint';
}

function resolvePreviewData(fileName: string, fileType: string | undefined, fileData: string | undefined, dataSource: PreviewableFileDataSource): ResolvedPreviewData {
  if (!fileData) {
    return { canPreview: false, isImage: false, previewKind: 'none' };
  }

  const parsedDataUrl = dataSource === 'data-url' ? parseDataUrl(fileData) : null;
  const effectiveMimeType = parsedDataUrl?.mimeType || inferMimeType(fileName, fileType);

  if (isImageType(fileName, effectiveMimeType)) {
    const imageSrc = parsedDataUrl?.dataUrl || `data:${effectiveMimeType};base64,${fileData}`;
    return {
      canPreview: true,
      isImage: true,
      previewKind: 'image',
      previewFileData: imageSrc,
      previewFileType: effectiveMimeType,
    };
  }

  if (isPdfType(fileName, effectiveMimeType)) {
    const pdfPayload = parsedDataUrl?.payload || fileData;
    return {
      canPreview: true,
      isImage: false,
      previewKind: 'pdf',
      previewFileData: base64ToArrayBuffer(pdfPayload),
      previewFileType: 'application/pdf',
    };
  }

  if (isExcelType(fileName, effectiveMimeType)) {
    const base64Payload = parsedDataUrl?.payload || fileData;
    return {
      canPreview: true,
      isImage: false,
      previewKind: 'table',
      previewFileData: base64ToArrayBuffer(base64Payload),
      previewFileType: effectiveMimeType,
    };
  }

  if (isTextType(fileName, effectiveMimeType)) {
    let textData = fileData;
    if (parsedDataUrl) {
      textData = parsedDataUrl.isBase64 ? base64ToUtf8(parsedDataUrl.payload) : decodeURIComponent(parsedDataUrl.payload);
    } else if (dataSource === 'uploaded') {
      // For uploaded files, data might be base64-encoded text (e.g. conversion fallback)
      // Try to detect and decode: if the string looks like base64, decode it
      const looksLikeBase64 = /^[A-Za-z0-9+/\s]+=*$/.test(fileData.trim()) && fileData.length > 20;
      if (looksLikeBase64) {
        try {
          const decoded = base64ToUtf8(fileData);
          // If decoded result has sensible text chars (not binary garbage), use it
          if (!/[\x00-\x08\x0E-\x1F]/.test(decoded.substring(0, 200))) {
            textData = decoded;
          }
        } catch {
          // Not valid base64, keep as-is
        }
      }
    }
    // CSV/TSV are text but should be treated as table
    const kind = isTableType(fileName, effectiveMimeType) ? 'table' : 'text';
    return {
      canPreview: true,
      isImage: false,
      previewKind: kind,
      previewFileData: textData,
      previewFileType: effectiveMimeType,
    };
  }

  // Convertible document types (Word, PowerPoint) — FilePreview will handle conversion
  if (isConvertibleDocType(fileName, effectiveMimeType)) {
    const base64Payload = parsedDataUrl?.payload || fileData;
    return {
      canPreview: true,
      isImage: false,
      previewKind: 'document' as const,
      previewFileData: base64ToArrayBuffer(base64Payload),
      previewFileType: effectiveMimeType,
    };
  }

  return { canPreview: false, isImage: false, previewKind: 'none' as const };
}

function FilePreviewDialog({
  fileName,
  isOpen,
  onClose,
  previewFileData,
  previewFileType,
  onRequestFullscreenTable,
  onRequestFullscreenText,
}: {
  fileName: string;
  isOpen: boolean;
  onClose: () => void;
  previewFileData?: string | ArrayBuffer;
  previewFileType?: string;
  onRequestFullscreenTable?: (data: FullscreenTableData) => void;
  onRequestFullscreenText?: (data: FullscreenTextData) => void;
}) {
  const previewInstanceKey = React.useMemo(() => {
    const dataKey = typeof previewFileData === 'string'
      ? `s:${previewFileData.length}:${previewFileData.slice(0, 32)}`
      : previewFileData instanceof ArrayBuffer
        ? `b:${previewFileData.byteLength}`
        : 'none';
    return `${fileName}|${previewFileType || ''}|${dataKey}`;
  }, [fileName, previewFileData, previewFileType]);

  const [shouldRender, setShouldRender] = React.useState(isOpen);
  const [isVisible, setIsVisible] = React.useState(isOpen);
  // When true, skip the exit animation so the fullscreen overlay can take over seamlessly
  const skipExitAnimRef = React.useRef(false);

  const handleOpenFullscreen = React.useCallback(() => {
    skipExitAnimRef.current = true;
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (isOpen) {
      skipExitAnimRef.current = false;
      setShouldRender(true);
      const rafId = window.requestAnimationFrame(() => setIsVisible(true));
      return () => window.cancelAnimationFrame(rafId);
    }

    if (!shouldRender) return undefined;

    // Instant dismiss when transitioning to fullscreen – no visible exit animation
    if (skipExitAnimRef.current) {
      setIsVisible(false);
      setShouldRender(false);
      return undefined;
    }

    setIsVisible(false);
    const timeoutId = window.setTimeout(() => {
      setShouldRender(false);
    }, PREVIEW_DIALOG_ANIMATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen, shouldRender]);

  React.useEffect(() => {
    if (!shouldRender) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose, shouldRender]);

  if (!previewFileData || typeof document === 'undefined' || !shouldRender) return null;

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.78)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      initial={false}
      animate={{ opacity: isVisible ? 1 : 0 }}
      transition={{ duration: isVisible ? 0.22 : 0.32, ease: 'easeOut' }}
    >
      <motion.div
        className="relative w-full max-w-5xl border border-white/10 bg-[var(--color-secondary)] shadow-2xl"
        style={{ maxHeight: '90vh', borderRadius: '0px' }}
        onClick={(event) => event.stopPropagation()}
        initial={false}
        animate={{
          opacity: isVisible ? 1 : 0,
          scale: isVisible ? 1 : 0.96,
          y: isVisible ? 0 : 18,
        }}
        transition={{ duration: isVisible ? 0.26 : 0.32, ease: [0.22, 1, 0.36, 1] }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close file preview"
          className="absolute right-4 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-md border border-white/10 bg-black/40 text-white transition-opacity hover:opacity-100"
          style={{ opacity: 0.85 }}
        >
          <Cross2Icon width={18} height={18} />
        </button>

        <div className="border-b border-[var(--color-accent)]/20 px-6 py-3 pr-14">
          <div className="truncate text-sm font-semibold text-[var(--color-text)]" title={fileName}>
            {fileName}
          </div>
        </div>

        <div className="overflow-auto px-6 py-4" style={{ maxHeight: 'calc(90vh - 72px)', color: 'var(--color-text)' }}>
          <FilePreview
            key={previewInstanceKey}
            fileName={fileName}
            fileData={previewFileData}
            fileType={previewFileType}
            onOpenFullscreen={handleOpenFullscreen}
            onRequestFullscreenTable={onRequestFullscreenTable}
            onRequestFullscreenText={onRequestFullscreenText}
          />
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

export function PreviewableFileAttachment({
  fileName,
  fileType,
  fileData,
  dataSource = 'data-url',
  children,
}: PreviewableFileAttachmentProps) {
  const dispatch = useDispatch();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [fullscreenTable, setFullscreenTable] = React.useState<FullscreenTableData | null>(null);
  const [fullscreenText, setFullscreenText] = React.useState<FullscreenTextData | null>(null);
  // Direct fullscreen for PDF/table — skips mini overlay
  const [directFullscreen, setDirectFullscreen] = React.useState(false);
  // Document conversion (PPTX/DOCX): show converting spinner, then open ImageModal directly
  const [documentDirectOpen, setDocumentDirectOpen] = React.useState(false);

  const resolvedPreview = React.useMemo(
    () => resolvePreviewData(fileName, fileType, fileData, dataSource),
    [dataSource, fileData, fileName, fileType]
  );

  const previewInstanceKey = React.useMemo(() => {
    const previewData = resolvedPreview.previewFileData;
    const dataKey = typeof previewData === 'string'
      ? `s:${previewData.length}:${previewData.slice(0, 32)}`
      : previewData instanceof ArrayBuffer
        ? `b:${previewData.byteLength}`
        : 'none';
    return `${fileName}|${resolvedPreview.previewFileType || ''}|${dataKey}`;
  }, [fileName, resolvedPreview.previewFileData, resolvedPreview.previewFileType]);

  const pdfObjectUrlRef = React.useRef<string | null>(null);
  const pdfObjectSourceRef = React.useRef<ArrayBuffer | null>(null);

  const pdfViewerSrc = React.useMemo(() => {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const isPdf = resolvedPreview.previewKind === 'pdf' || ext === 'pdf';
    if (!isPdf || !(resolvedPreview.previewFileData instanceof ArrayBuffer)) {
      return null;
    }
    if (pdfObjectUrlRef.current && pdfObjectSourceRef.current === resolvedPreview.previewFileData) {
      return pdfObjectUrlRef.current;
    }
    const url = URL.createObjectURL(new Blob([resolvedPreview.previewFileData], { type: 'application/pdf' }));
    pdfObjectUrlRef.current = url;
    pdfObjectSourceRef.current = resolvedPreview.previewFileData;
    return url;
  }, [resolvedPreview, fileName]);

  const openPreview = React.useCallback(() => {
    if (!resolvedPreview.canPreview || !resolvedPreview.previewFileData) return;

    const kind = resolvedPreview.previewKind;
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    // Images → fullscreen ImageModal
    if (resolvedPreview.isImage && typeof resolvedPreview.previewFileData === 'string') {
      dispatch(openImage({ src: resolvedPreview.previewFileData, type: 'image', name: fileName }));
      return;
    }

    // PDFs → fullscreen ImageModal (PDF mode)
    if ((kind === 'pdf' || ext === 'pdf') && pdfViewerSrc) {
      dispatch(openImage({ src: pdfViewerSrc, type: 'pdf', name: fileName }));
      return;
    }

    // Tables (Excel/CSV/TSV) → direct fullscreen with FilePreview inside
    if (kind === 'table' || /^(xlsx|xls|csv|tsv)$/.test(ext)) {
      setDirectFullscreen(true);
      return;
    }

    // Convertible documents (Word/PPT) → headless conversion then open ImageModal directly
    if (kind === 'document' || /^(docx?|pptx?)$/.test(ext)) {
      setDocumentDirectOpen(true);
      return;
    }

    // Text/other → mini dialog (existing behaviour)
    setIsDialogOpen(true);
  }, [dispatch, resolvedPreview, pdfViewerSrc, fileName]);

  const closePreview = React.useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleConvertedPdfReady = React.useCallback((pdfUrl: string) => {
    setDocumentDirectOpen(false);
    dispatch(openImage({ src: pdfUrl, type: 'pdf', name: fileName, useCustomPdfHeader: true }));
  }, [dispatch, fileName]);

  const handleRequestFullscreenTable = React.useCallback((data: FullscreenTableData) => {
    setFullscreenTable(data);
  }, []);

  const handleRequestFullscreenText = React.useCallback((data: FullscreenTextData) => {
    setFullscreenText(data);
  }, []);

  const renderTable = (headers: string[], bodyRows: string[][]) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
      <thead>
        <tr style={{ background: 'var(--form-accent)', position: 'sticky', top: 0 }}>
          {headers.map((header, idx) => (
            <th key={idx} style={{
              padding: '8px 12px',
              textAlign: 'left',
              borderBottom: `2px solid ${isDark ? '#444' : '#d0d0d0'}`,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              color: isDark ? '#e0e0e0' : '#1a1a1a'
            }}>
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((row, rowIdx) => (
          <tr key={rowIdx} style={{
            background: rowIdx % 2 === 0 ? (isDark ? '#1a1a1a' : '#ffffff') : (isDark ? '#222' : '#f9f9f9')
          }}>
            {row.map((cell, cellIdx) => (
              <td key={cellIdx} style={{
                padding: '8px 12px',
                borderBottom: `1px solid ${isDark ? '#333' : '#e0e0e0'}`,
                whiteSpace: 'nowrap',
                color: isDark ? '#d4d4d4' : '#333'
              }}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <>
      {children({
        canPreview: resolvedPreview.canPreview,
        isImage: resolvedPreview.isImage,
        openPreview,
        previewFileData: resolvedPreview.previewFileData,
        previewFileType: resolvedPreview.previewFileType,
        previewKind: resolvedPreview.previewKind,
      })}
      <FilePreviewDialog
        fileName={fileName}
        isOpen={isDialogOpen}
        onClose={closePreview}
        previewFileData={resolvedPreview.previewFileData}
        previewFileType={resolvedPreview.previewFileType}
        onRequestFullscreenTable={handleRequestFullscreenTable}
        onRequestFullscreenText={handleRequestFullscreenText}
      />

      {/* Fullscreen table modal - rendered outside the mini dialog so it survives dialog close */}
      <FullscreenDataModal
        isOpen={!!fullscreenTable}
        onClose={() => setFullscreenTable(null)}
        title={fullscreenTable?.title || fileName}
      >
        {fullscreenTable && (
          <div style={{ overflowX: 'auto' }}>
            {renderTable(fullscreenTable.rows[0] || [], fullscreenTable.rows.slice(1))}
            <div style={{
              padding: '8px',
              textAlign: 'center',
              background: 'var(--form-accent)',
              fontSize: '0.85em',
              color: isDark ? '#aaa' : '#666'
            }}>
              {fullscreenTable.rows.length - 1} row{fullscreenTable.rows.length - 1 !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </FullscreenDataModal>

      {/* Fullscreen text modal - rendered outside the mini dialog so it survives dialog close */}
      <FullscreenDataModal
        isOpen={!!fullscreenText}
        onClose={() => setFullscreenText(null)}
        title={fullscreenText?.title || fileName}
      >
        {fullscreenText && (
          <pre style={{
            padding: '16px',
            margin: 0,
            fontSize: '0.85em',
            background: isDark ? '#1a1a1a' : '#f9f9f9',
            color: isDark ? '#e0e0e0' : '#333',
            overflow: 'auto',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            borderRadius: '8px'
          }}>
            {fullscreenText.text}
          </pre>
        )}
      </FullscreenDataModal>

      {/* Direct fullscreen for table/Excel files — no mini overlay */}
      {resolvedPreview.previewFileData && (
        <FullscreenDataModal
          isOpen={directFullscreen}
          onClose={() => setDirectFullscreen(false)}
          title={fileName}
          size="viewer"
        >
          <FilePreview
            key={previewInstanceKey}
            fileName={fileName}
            fileData={resolvedPreview.previewFileData}
            fileType={resolvedPreview.previewFileType}
            fullscreenMode
          />
        </FullscreenDataModal>
      )}

      {/* Document conversion (PPTX/DOCX): show converting spinner in modal, then jump directly to ImageModal */}
      {documentDirectOpen && resolvedPreview.previewFileData && (
        <FullscreenDataModal
          isOpen={documentDirectOpen}
          onClose={() => setDocumentDirectOpen(false)}
          title={fileName}
          size="viewer"
        >
          <FilePreview
            key={`doc-convert-${previewInstanceKey}`}
            fileName={fileName}
            fileData={resolvedPreview.previewFileData}
            fileType={resolvedPreview.previewFileType}
            onConvertedPdfReady={handleConvertedPdfReady}
          />
        </FullscreenDataModal>
      )}
    </>
  );
}
